import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { FeedbackState } from '../types';
import { DELAYS } from '../utils/constants';

// ============================================
// Types
// ============================================

interface UIContextType {
  // Sidebar
  isMobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleMobileSidebar: () => void;

  // Feedback/Toast
  feedback: FeedbackState | null;
  showFeedback: (message: string, type: FeedbackState['type']) => void;
  clearFeedback: () => void;

  // Loading states
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
}

// ============================================
// Context
// ============================================

const UIContext = createContext<UIContextType | null>(null);

// ============================================
// Provider
// ============================================

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  // Sidebar state
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Feedback/Toast state
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  
  // Global loading state
  const [globalLoading, setGlobalLoading] = useState(false);

  // Toggle sidebar
  const toggleMobileSidebar = useCallback(() => {
    setMobileSidebarOpen(prev => !prev);
  }, []);

  // Show feedback with auto-dismiss
  const showFeedback = useCallback((message: string, type: FeedbackState['type']) => {
    setFeedback({ message, type });
    
    // Auto-dismiss after delay
    setTimeout(() => {
      setFeedback(null);
    }, DELAYS.TOAST_DURATION);
  }, []);

  // Clear feedback manually
  const clearFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  const value: UIContextType = {
    isMobileSidebarOpen,
    setMobileSidebarOpen,
    toggleMobileSidebar,
    feedback,
    showFeedback,
    clearFeedback,
    globalLoading,
    setGlobalLoading,
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export { UIContext };
export default UIContext;
